import React, { useState } from 'react';
import { BookOpen, FileText, Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { blogPosts } from '@/data/blogPosts';

const Blogs = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blogs"
        description="Explore Boxd Logistics blogs on RTO reduction, courier strategy, delivery operations, COD best practices, and shipping cost optimization."
        keywords="ecommerce logistics blogs, shipping aggregator insights, courier strategy, RTO reduction, NDR management, COD best practices, shipping cost optimization, logistics trends India"
        canonical="https://boxdlogistics.in/blogs"
        image="https://boxdlogistics.in/logo.jpg"
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Boxd Logistics Blog',
          description:
            'Expert logistics and eCommerce shipping insights including RTO reduction, courier selection, COD operations, and cost optimization.',
          url: 'https://boxdlogistics.in/blogs',
          publisher: {
            '@type': 'Organization',
            name: 'Boxd Logistics',
            url: 'https://boxdlogistics.in'
          },
          blogPost: blogPosts.map((post) => ({
            '@type': 'BlogPosting',
            headline: post.title,
            url: `https://boxdlogistics.in/blogs/${post.slug}`,
            image: post.image,
            author: {
              '@type': 'Person',
              name: post.author
            },
            keywords: post.tags.join(', ')
          }))
        }}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-blue-300/10 rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-100/20 to-blue-200/10 rounded-full blur-3xl animate-float-slower"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full mb-6 animate-bounce-gentle">
            <BookOpen className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
              Logistics Insights & Guides
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert advice, industry trends, and best practices to help you optimize your eCommerce shipping operations
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Link
                key={index}
                to={`/blogs/${post.slug}`}
                className="group relative bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-2"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Gradient Header */}
                <div className={`h-48 bg-gradient-to-br ${post.gradient} flex items-center justify-center relative overflow-hidden`}>
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-black/15"></div>
                  <FileText className="relative z-10 w-16 h-16 text-white transform transition-transform duration-300 group-hover:scale-110" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-xs font-bold text-gray-800">{post.category}</span>
                  </div>
                  
                  {/* Animated overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </div>
                
                <div className="p-6">
                  {/* Meta Info */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="w-3 h-3 mr-1" />
                      {post.date}
                    </div>
                    <div className="flex items-center text-xs text-blue-800 font-semibold">
                      <Clock className="w-3 h-3 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-800 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  {/* Excerpt */}
                  <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag, tagIndex) => (
                      <span 
                        key={tagIndex}
                        className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-900 text-xs font-semibold rounded-full"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Read More */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-sm text-gray-500">By {post.author}</span>
                    <span className="flex items-center text-blue-800 font-semibold group-hover:text-blue-900 transition-colors">
                      Read More 
                      <ArrowRight className={`w-4 h-4 ml-1 transform transition-transform ${
                        hoveredIndex === index ? 'translate-x-1' : ''
                      }`} />
                    </span>
                  </div>
                </div>
                
                {/* Hover Accent Line */}
                <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${post.gradient} group-hover:w-full transition-all duration-500`}></div>
              </Link>
            ))}
          </div>
          
        </div>
      </section>
      
      <Footer />
      
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
        
        @keyframes float-slower {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(30px) translateX(-20px); }
        }
        
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }
        
        .animate-bounce-gentle {
          animation: bounce-gentle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Blogs;